"use client";

// FILE: src/features/nmck-calculator/components/chop-request-step.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Step 4 «Запрос КП у ЧОП» — 3 карточки ЧОП, форма контактов заказчика, отправка запросов
// SCOPE: Загрузка ЧОП из /api/calculator/chops, замена через поиск, отправка через POST /api/calculator/send-requests
// INPUT: calculationId, nmckTotal, posts, period, region, onBack
// OUTPUT: Состояния idle/loading/sending/sent/error
// KEYWORDS: DOMAIN(NMCC): ChopRequest; TECH(React): ClientComponent; STATE(idle|loading|sending|sent|error)
// END_MODULE_CONTRACT

import { useState, useEffect } from "react";
import { Loader2, Send, CheckCircle2, XCircle, ArrowLeft, Mail, Building2, RefreshCw, Plus } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { Input } from "@oxpa/shared/ui/input";
import { ChopCard } from "./chop-card";
import { ChopSearchModal } from "./chop-search-modal";
import type { ChopCandidate } from "@/app/api/calculator/chops/route";

type StepState = "idle" | "loading" | "sending" | "sent" | "error";

interface ChopRequestStepProps {
  calculationId?: string;
  nmckTotal?: number;
  posts?: string;
  period?: string;
  region?: string;
  onBack?: () => void;
}

export function ChopRequestStep({ calculationId, nmckTotal, posts, period, region, onBack }: ChopRequestStepProps) {
  const [chops, setChops] = useState<ChopCandidate[]>([]);
  const [state, setState] = useState<StepState>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [replaceIndex, setReplaceIndex] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [results, setResults] = useState<Array<{ chop: string; email: string; success: boolean; error?: string }>>([]);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailValid = EMAIL_RE.test(customerEmail);
  const isFormValid = customerName.trim().length > 0 && customerEmail.trim().length > 0 && isEmailValid;

  useEffect(() => {
    if (state === "idle") {
      loadChops();
    }
  }, [state]);

  async function loadChops() {
    setState("loading");
    try {
      const res = await fetch("/api/calculator/chops");
      const json = await res.json();
      if (json.success) {
        setChops(json.data as ChopCandidate[]);
        setState("idle");
      } else {
        setErrorMsg(json.error || "Ошибка загрузки ЧОП");
        setState("error");
      }
    } catch {
      setErrorMsg("Не удалось загрузить список ЧОП");
      setState("error");
    }
  }

  function handleRemove(index: number) {
    setChops((prev) => prev.filter((_, i) => i !== index));
  }

  function handleReplace(index: number) {
    setReplaceIndex(index);
  }

  function handleReplaceSelect(newChop: ChopCandidate) {
    if (isAdding) {
      setChops((prev) => [...prev, { ...newChop, is_fixed: false }]);
      setIsAdding(false);
    } else if (replaceIndex !== null) {
      setChops((prev) => {
        const next = [...prev];
        next[replaceIndex] = { ...newChop, is_fixed: false };
        return next;
      });
      setReplaceIndex(null);
    }
  }

  function handleAddChop() {
    setIsAdding(true);
  }

  async function handleSend() {
    setState("sending");
    try {
      const body = {
        calculation_id: calculationId || `calc_${Date.now()}`,
        selected_chops: chops.map((c) => ({
          id: c.id,
          email: c.contact_email || "",
          name: c.name_short,
        })),
        customer_contacts: {
          name: customerName,
          email: customerEmail,
          phone: customerPhone || undefined,
        },
        nmck: nmckTotal?.toString(),
        posts,
        period,
        region,
      };

      const res = await fetch("/api/calculator/send-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();

      if (json.success) {
        const rawResults = json.results || [];
        setResults(rawResults.map((r: any) => ({
          chop: r.chop || r.name || r.chop_id || "—",
          email: r.email || "",
          success: r.status === "sent" || r.success === true,
          error: r.error || r.message || undefined,
        })));
        setState("sent");
      } else {
        setErrorMsg(json.error || "Ошибка отправки");
        setState("error");
      }
    } catch {
      setErrorMsg("Не удалось отправить запросы. Проверьте соединение.");
      setState("error");
    }
  }

  if (state === "loading") {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-white/40">
        <Loader2 className="h-8 w-8 animate-spin mb-4" />
        <p className="text-xs uppercase tracking-widest font-mono">Подбор ЧОП...</p>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <XCircle className="h-8 w-8 text-red-500 mb-4" />
        <p className="text-sm text-red-400 font-mono mb-4">{errorMsg}</p>
        <Button
          type="button"
          variant="outline"
          onClick={loadChops}
          className="rounded-none border-white/20 text-white hover:text-white h-10 px-4 text-[10px] uppercase tracking-widest"
        >
          <RefreshCw className="h-3 w-3 mr-1.5" />
          Повторить
        </Button>
      </div>
    );
  }

  if (state === "sent") {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
        <div className="rounded-full bg-green-500/10 border border-green-500/30 p-4">
          <CheckCircle2 className="h-10 w-10 text-green-400" />
        </div>
        <h3 className="text-lg font-bold text-white font-mono">Запросы отправлены</h3>
        <p className="text-sm text-white/60 max-w-md font-mono">
          Письма направлены в {chops.map((c) => c.name_short).join(", ")}.
          Ожидайте ответа на указанный email.
        </p>
        {results.length > 0 && (
          <div className="w-full max-w-md space-y-1 pt-2">
            {results.map((r, i) => (
              <div
                key={i}
                data-testid="chop-status"
                className={`flex items-center gap-2 text-xs font-mono p-2 border ${
                  r.success
                    ? "text-green-400 border-green-500/20 bg-green-500/5"
                    : "text-red-400 border-red-500/20 bg-red-500/5"
                }`}
              >
                {r.success ? (
                  <CheckCircle2 className="h-3 w-3 shrink-0" />
                ) : (
                  <XCircle className="h-3 w-3 shrink-0" />
                )}
                <span className="truncate">{r.chop}: {r.success ? "Отправлено" : r.error || "Ошибка"}</span>
              </div>
            ))}
          </div>
        )}
        <div className="space-y-1 text-xs text-white/40 font-mono">
          <p><Mail className="h-3 w-3 inline mr-1" />{customerEmail}</p>
        </div>
        {onBack && (
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="rounded-none border-white/20 text-white/60 hover:text-white h-10 px-4 text-[10px] uppercase tracking-widest mt-4"
          >
            <ArrowLeft className="h-3 w-3 mr-1.5" />
            Назад к результату
          </Button>
        )}
      </div>
    );
  }

  const excludeIds = chops.map((c) => c.id);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 border-b border-white/10 pb-4">
        <Building2 className="h-5 w-5 text-white" />
        <h3 className="text-sm uppercase tracking-widest text-white font-bold">
          Шаг 4: Запрос коммерческих предложений
        </h3>
      </div>

      <div className="space-y-2">
        <p className="text-xs text-white/50 font-mono">
          Система подобрала ЧОП из рейтинга. Вы можете заменить любую организацию.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {chops.map((chop, i) => (
            <ChopCard
              key={`${chop.id}-${i}`}
              chop={chop}
              onReplace={() => handleReplace(i)}
              onRemove={chops.length > 1 ? () => handleRemove(i) : undefined}
              disabled={state === "sending"}
            />
          ))}
        </div>
        {chops.length < 5 && (
          <div className="flex justify-center pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddChop}
              disabled={state === "sending"}
              className="rounded-none border-dashed border-white/20 text-white/50 hover:text-white hover:border-white/40 h-9 px-5 text-[10px] uppercase tracking-widest"
            >
              <Plus className="h-3 w-3 mr-1.5" />
              Добавить ЧОП
            </Button>
          </div>
        )}
      </div>

      <div className="border border-white/10 bg-white/5 p-4 space-y-4">
        <h4 className="text-[10px] uppercase tracking-widest text-white/60 font-bold">
          <Mail className="h-3 w-3 inline mr-1.5" />
          Контакты заказчика
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">
              Имя / Организация
            </label>
            <Input
              name="customer_name"
              placeholder="Иванов Иван"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              disabled={state === "sending"}
              className="rounded-none bg-black border-white/20 text-white font-mono h-10"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">
              Email *
            </label>
            <Input
              name="customer_email"
              type="email"
              placeholder="ivanov@example.com"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              disabled={state === "sending"}
              className={`rounded-none bg-black border-white/20 text-white font-mono h-10 ${customerEmail && !isEmailValid ? "border-red-500" : ""}`}
            />
            {customerEmail && !isEmailValid && (
              <p className="text-[10px] text-red-400 mt-1">Ошибка: неверный формат email</p>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">
              Телефон (опционально)
            </label>
            <Input
              name="customer_phone"
              type="tel"
              placeholder="+7 (999) 123-45-67"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              disabled={state === "sending"}
              className="rounded-none bg-black border-white/20 text-white font-mono h-10"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        {onBack ? (
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={state === "sending"}
            className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/10 h-12 w-12 p-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        ) : <div />}
        <Button
          type="button"
          disabled={!isFormValid || state === "sending" || chops.length === 0}
          onClick={handleSend}
          className="rounded-none bg-green-500 text-black hover:bg-green-400 disabled:bg-white/10 disabled:text-white/30 uppercase tracking-widest text-[10px] h-12 px-8 font-bold"
        >
          {state === "sending" ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Отправка...</>
          ) : (
            <><Send className="mr-2 h-4 w-4" /> Отправить запросы</>
          )}
        </Button>
      </div>

      <ChopSearchModal
        open={replaceIndex !== null || isAdding}
        onOpenChange={(open) => { if (!open) { setReplaceIndex(null); setIsAdding(false); } }}
        onSelect={handleReplaceSelect}
        excludeIds={excludeIds}
      />
    </div>
  );
}

// trigger deploy 2026-07-02